/* eslint-disable no-console */
import * as Sentry from '@sentry/react';
import React, { useEffect, useRef } from 'react';
import { useRouteError } from 'react-router-dom';
import { Col, Row } from 'reactstrap';

import { CustomButton } from '@shared/buttons';
import { Error500Icon } from '@shared/icons';

import LoadingBlock from '../LoadingBlock';

import './style.scss';

const RELOAD_STATE_KEY = 'reforal:chunkReloadState';
const MAX_RELOAD_ATTEMPTS = 1;
const RELOAD_WINDOW_MS = 30 * 1000;

type ReloadState = {
  count: number;
  firstAt: number;
};

const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false;

  const anyErr = error as any;
  const name: string = anyErr?.name || anyErr?.error?.name || '';
  const message: string = anyErr?.message
    || anyErr?.error?.message
    || (typeof anyErr === 'string' ? anyErr : '')
    || '';

  if (name === 'ChunkLoadError') return true;

  return (
    /Loading chunk [\w-]+ failed/i.test(message)
    || /Loading CSS chunk [\w-]+ failed/i.test(message)
    || message.includes('CssChunkLoadError')
    || message.includes('Failed to fetch dynamically imported module')
    || message.includes('Importing a module script failed')
  );
};

const readReloadState = (): ReloadState => {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_STATE_KEY);
    if (!raw) return { count: 0, firstAt: 0 };
    const parsed = JSON.parse(raw) as ReloadState;
    if (
      !parsed
      || typeof parsed.count !== 'number'
      || typeof parsed.firstAt !== 'number'
    ) {
      return { count: 0, firstAt: 0 };
    }
    if (Date.now() - parsed.firstAt > RELOAD_WINDOW_MS) {
      return { count: 0, firstAt: 0 };
    }
    return parsed;
  } catch {
    return { count: 0, firstAt: 0 };
  }
};

const writeReloadState = (state: ReloadState) => {
  try {
    window.sessionStorage.setItem(RELOAD_STATE_KEY, JSON.stringify(state));
  } catch {
    // ignore — storage may be unavailable
  }
};

const clearReloadState = () => {
  try {
    window.sessionStorage.removeItem(RELOAD_STATE_KEY);
  } catch {
    // ignore
  }
};

const CACHE_BUST_PARAM = '_cb';

// Reloads with a unique query param so the browser cannot serve the cached
// HTML. Stale `index.html` in the browser/HTTP cache is the root reason
// chunk errors recur even after a plain reload — busting the URL forces a
// fresh fetch of the latest index (which references the latest chunks).
const cacheBustedReload = () => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(CACHE_BUST_PARAM, Date.now().toString(36));
    window.location.replace(url.toString());
  } catch {
    try {
      window.location.reload();
    } catch {
      // last resort — should never happen
    }
  }
};

const clearBrowserCachesAndReload = () => {
  if (
    typeof window !== 'undefined'
    && window.caches
    && typeof window.caches.keys === 'function'
  ) {
    window.caches
      .keys()
      .then((keys) => Promise.all(
        keys.map((k) => window.caches.delete(k).catch(() => false)),
      ),
      )
      .catch(() => undefined)
      .finally(cacheBustedReload);
    return;
  }

  cacheBustedReload();
};

const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError();
  const chunkError = isChunkLoadError(error);
  const state = chunkError ? readReloadState() : { count: 0, firstAt: 0 };
  const canAutoReload = chunkError && state.count < MAX_RELOAD_ATTEMPTS;

  // Guard against React StrictMode double-invocation of effects in dev, and
  // any other path that could fire the side-effect twice in a single mount.
  const didRunSideEffectRef = useRef(false);

  useEffect(() => {
    if (didRunSideEffectRef.current) return;
    didRunSideEffectRef.current = true;

    if (chunkError) {
      if (canAutoReload) {
        const next: ReloadState = {
          count: state.count + 1,
          firstAt: state.firstAt || Date.now(),
        };
        writeReloadState(next);
        console.warn(
          '[RouteErrorBoundary] ChunkLoadError detected — reloading to fetch latest assets.',
          error,
        );
        clearBrowserCachesAndReload();
      } else {
        console.error(
          '[RouteErrorBoundary] Repeated ChunkLoadError after reload attempts — showing fallback.',
          error,
        );
        try {
          Sentry.captureException(error, {
            tags: { boundary: 'route', kind: 'chunk-load-persistent' },
          });
        } catch {
          // ignore
        }
      }
      return;
    }

    // Non-chunk errors: report to Sentry (the top-level Sentry.ErrorBoundary
    // does not see errors that React Router's errorElement has already caught).
    try {
      Sentry.captureException(error, { tags: { boundary: 'route' } });
    } catch {
      // ignore
    }
    console.error('[RouteErrorBoundary] Unhandled route error:', error);
  }, [ chunkError, canAutoReload, error, state.count, state.firstAt ]);

  if (chunkError && canAutoReload) {
    return <LoadingBlock enabled withReforalLogo />;
  }

  const handleRetry = () => {
    clearReloadState();
    clearBrowserCachesAndReload();
  };

  const handleGoHome = () => {
    clearReloadState();
    try {
      window.location.assign('/');
    } catch {
      window.location.reload();
    }
  };

  const title = chunkError ? 'Update available' : 'Something went wrong';
  const message = chunkError
    ? 'We could not load the latest version of the app. Please reload to try again.'
    : 'An unexpected error occurred. Please try again — if the problem persists, contact support.';

  return (
    <div className="error-container">
      <Row className="mb-5">
        <Col>
          <Error500Icon />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <div className="title">{title}</div>
        </Col>
      </Row>
      <Row className="mb-5">
        <Col>
          <div className="message">{message}</div>
        </Col>
      </Row>
      <Row>
        <Col className="d-flex justify-content-center gap-3">
          <CustomButton
            className="btn-lg go-to-home-btn"
            color="primary"
            onClick={handleRetry}
          >
            Reload
          </CustomButton>
          <CustomButton
            className="btn-lg go-to-home-btn"
            color="secondary"
            outline
            onClick={handleGoHome}
          >
            Go to home
          </CustomButton>
        </Col>
      </Row>
    </div>
  );
};

export default RouteErrorBoundary;
